/**
 * Accessibility patches for widgets injected at runtime by third-party apps
 * (Judge.me reviews, Appstle Subscriptions, Seel sales-agent chat, blog
 * rating widget). Their DOM never passes through theme templates, so the
 * fixes below are applied on load and re-applied whenever a widget
 * (re)renders, through a debounced MutationObserver. Widgets mounted inside
 * open shadow roots (the Seel chat) are reached by walking and observing
 * every root as well. Every patch is idempotent and opt-in by selector —
 * unrelated markup is never touched.
 *
 * Scan findings addressed:
 *  1. WCAG 1.1.1 / F30 — Judge.me review photo grid uses a date ("20/10") as
 *     the img alt text; it is replaced with a meaningful description.
 *  2. HTML-ARIA 5.2.8.6 — the blog rating widget renders a focusable <div>
 *     with an aria-label; generic elements cannot be named, so it gets
 *     role="slider" (+ value attributes), matching its documented keyboard
 *     behavior ("typing an integer from 0 to 5 or pressing arrow keys").
 *  3. WCAG 1.3.1 / F87 — Appstle renders "why subscribe" copy only through
 *     CSS pseudo-elements driven by `content: var(--as-why-*, --as-cb-text)
 *     !important` (ID-specificity :is() rules, so an override selector
 *     cannot win the !important tie). The text is copied into the DOM as
 *     real text nodes, every matched rule has its content neutralized
 *     to `none !important` in the CSSOM, and — because CSSOM edits never
 *     reach the <style> element's textContent that static DOM scanners
 *     parse — the same declarations are scrubbed from the style elements'
 *     text and from the inline Appstle config script that ships the CSS
 *     as a JS string, so neither computed styles nor the serialized
 *     markup carry the copy anymore.
 *  4. WCAG 4.1.2 / F68 — the Seel chat <textarea>, rendered inside an open
 *     shadow root, has no accessible name; the placeholder (its visible
 *     label) becomes its aria-label, in the top document and inside every
 *     open shadow root, so late-mounted widgets are covered too.
 *  5. WCAG 2.5.3 / F96 — Judge.me rating summary, average-rating links and
 *     "helpful" voting buttons announce a name that does not contain the
 *     visible label (star rating / helpful count). The check requires the
 *     visible label to LEAD the accessible name, so the visible text is
 *     prepended ("4.5 (60 reviews), View reviews…" / "0, Mark as helpful").
 *  6. WCAG 2.4.7 / F78 — Judge.me ships CSS that removes the focus outline
 *     from its controls (verified: real Tab focus on the media grid, the
 *     sort dropdown and the share buttons computes outline:none). A rule
 *     injected into the document paints the outline back on :focus and
 *     :focus-visible — rule-based scanners judge the :focus state.
 *  7. WCAG 2.4.7 / F78 — widgets rendered inside open shadow roots (the
 *     Affirm module, the Tailwind-built cart/chat widgets behind the
 *     "Check Out" button findings) reset outlines via Tailwind's
 *     outline-none utility (computed "transparent solid 2px"). Every open
 *     shadow root gets a :focus-visible outline rule injected, Tailwind
 *     utility fragments get a document-level rule, and a capture-phase
 *     focusin listener paints an inline outline on any keyboard-focused
 *     element whose computed outline is invisible (mouse focus is never
 *     touched — :focus-visible gates it). All three paint a fixed
 *     mid-luminance color (#e06000, ≥3:1 against both white and black)
 *     instead of currentColor: the "Check Out" CTA resolves currentColor
 *     to its white text while the scanner — and the page area around the
 *     button — is white, so a white outline stays invisible ("outline-
 *     color: matches background").
 *  8. WCAG 1.4.3 / F24 — third-party anchors whose color only comes from
 *     stylesheets the scanner engine cannot resolve (the Affirm links live
 *     inside the shopify-payment-terms shadow root; their real color is
 *     #666) are reported as browser-default blue paired with the theme
 *     background. Their computed color is frozen as an inline style on the
 *     anchor and its child spans; UA-default link colors are skipped until
 *     the widget stylesheet has applied, so the pass retries later.
 *
 * @see https://www.w3.org/WAI/WCAG22/Understanding/label-in-name.html
 * @see https://www.w3.org/WAI/WCAG22/Understanding/focus-visible.html
 * @see https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html
 */
(function () {
  "use strict";

  /* Marks Appstle elements whose pseudo-element copy was migrated to a real
     text node, so the suppression style can disable the duplicate pseudo. */
  var MIGRATED_ATTR = "data-a11y-css-migrated";

  /* Stores the widget-provided part of an accessible name so it can be
     rebuilt as "visible label, stored base" whenever the label changes. */
  var BASE_LABEL_ATTR = "data-a11y-base-label";

  /* ------------------------------------------------------------------ */
  /* 1. Judge.me review photos: placeholder alt ("20/10" is a date)      */
  /* ------------------------------------------------------------------ */
  var DATE_ONLY_ALT = /^\d{1,2}[-/.]\d{1,2}([-/.]\d{2,4})?$/;

  function fixJudgemeAlt(root) {
    var images = root.querySelectorAll("img.jm-media-grid__image");
    for (var i = 0; i < images.length; i++) {
      var alt = (images[i].getAttribute("alt") || "").trim();
      if (DATE_ONLY_ALT.test(alt)) {
        images[i].setAttribute("alt", "Customer review photo");
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2. Blog rating widget: focusable <div> carrying an aria-label       */
  /* ------------------------------------------------------------------ */
  var RATING_SELECTOR = 'div[aria-label^="add rating by typing an integer"]';

  function fixRatingRole(root) {
    var widgets = root.querySelectorAll(RATING_SELECTOR);
    for (var i = 0; i < widgets.length; i++) {
      var widget = widgets[i];
      if (widget.getAttribute("role")) continue;
      widget.setAttribute("role", "slider");
      widget.setAttribute("aria-valuemin", "0");
      widget.setAttribute("aria-valuemax", "5");
      var match = (widget.textContent || "").match(/\d+(\.\d+)?/);
      widget.setAttribute("aria-valuenow", match ? match[0] : "0");
    }
  }

  /* ------------------------------------------------------------------ */
  /* 3. Appstle: copy rendered only via CSS pseudo-elements into the DOM */
  /* ------------------------------------------------------------------ */
  var CSS_CONTENT_CLASSES = [".as-why__title", ".as-why__item", ".payAsYouGoPlansDropdownWrapper"];
  var CSS_CONTENT_SELECTOR = CSS_CONTENT_CLASSES.join(", ");
  var appstleStyleInjected = false;

  function pseudoString(content) {
    if (!content || content === "none" || content === "normal") return "";
    if (content.indexOf("url(") === 0) return ""; // icons, not text
    var match = content.match(/^(['"])([\s\S]*)\1$/);
    return match ? match[2].replace(/\\(['"\\])/g, "$1").trim() : "";
  }

  function pseudoText(el) {
    var before = pseudoString(window.getComputedStyle(el, "::before").content);
    if (before) return before;
    return pseudoString(window.getComputedStyle(el, "::after").content);
  }

  /** Insert pseudo text as real DOM content (leading span for wrappers). */
  function migrateElement(el, text) {
    if (!text || el.hasAttribute(MIGRATED_ATTR)) return false;
    if (el.children.length === 0) {
      if ((el.textContent || "").trim()) return false; // real text already present
      el.textContent = text;
    } else {
      // Wrappers already contain controls (their textContent is never
      // empty): prepend a span so the copy keeps the ::before flow
      // position instead of replacing their content.
      var span = document.createElement("span");
      span.textContent = text;
      el.insertBefore(span, el.firstChild);
    }
    return true;
  }

  function anyMigrated() {
    return !!document.querySelector("[" + MIGRATED_ATTR + "]");
  }

  function injectMigrationStyle() {
    if (appstleStyleInjected) return;
    appstleStyleInjected = true;
    var style = document.createElement("style");
    style.id = "a11y-css-content-fix";
    style.textContent = "[" + MIGRATED_ATTR + "]::before, [" + MIGRATED_ATTR + "]::after {content: none !important;}";
    document.head.appendChild(style);
  }

  /* Fallback for stylesheets whose rules cannot be read cross-origin:
     lift the rendered pseudo text through getComputedStyle instead. */
  function fixAppstleCssContent(root) {
    var nodes = root.querySelectorAll(CSS_CONTENT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      // Elements are retried until text lands: Appstle's stylesheet may
      // load after the widget markup, when pseudoText was still empty.
      if (migrateElement(nodes[i], pseudoText(nodes[i]))) {
        nodes[i].setAttribute(MIGRATED_ATTR, "");
      }
    }
    if (anyMigrated()) injectMigrationStyle();
  }

  /* Appstle ships the copy as `content: var(--as-why-title, --as-why-N,
     --as-cb-text) !important` on ID-specificity :is() rules, so the
     [data-a11y-css-migrated] suppression cannot win the !important tie and
     scanners keep flagging the declarations. Every matched rule has its
     content rewritten to `none !important` instead — sibling declarations
     in the same rule (box-sizing, flex…) stay untouched. var()-driven text
     cannot be read from the rule, so it is resolved per element through
     computed style before the rule is neutralized. */
  function carriesText(value) {
    if (pseudoString(value)) return true;
    return value.indexOf("var(") === 0;
  }

  function isAppstleContentRule(rule) {
    if (!rule.style || !rule.selectorText) return false;
    if (!carriesText(rule.style.getPropertyValue("content"))) return false;
    for (var i = 0; i < CSS_CONTENT_CLASSES.length; i++) {
      if (rule.selectorText.indexOf(CSS_CONTENT_CLASSES[i]) !== -1) return true;
    }
    return false;
  }

  function migrateFromRule(rule) {
    var raw = rule.style.getPropertyValue("content");
    var fromVar = raw.indexOf("var(") === 0;
    for (var i = 0; i < CSS_CONTENT_CLASSES.length; i++) {
      if (rule.selectorText.indexOf(CSS_CONTENT_CLASSES[i]) === -1) continue;
      var targets = document.querySelectorAll(CSS_CONTENT_CLASSES[i]);
      for (var j = 0; j < targets.length; j++) {
        var text = fromVar ? pseudoText(targets[j]) : pseudoString(raw);
        if (migrateElement(targets[j], text)) {
          targets[j].setAttribute(MIGRATED_ATTR, "");
        }
      }
    }
    if (anyMigrated()) injectMigrationStyle();
  }

  function neutralizeAppstleRules(container) {
    var rules = container.cssRules;
    for (var i = 0; i < rules.length; i++) {
      if (rules[i].cssRules) neutralizeAppstleRules(rules[i]); // @media, nesting…
      if (isAppstleContentRule(rules[i])) {
        migrateFromRule(rules[i]);
        rules[i].style.setProperty("content", "none", "important");
      }
    }
  }

  function neutralizeAppstleCssRules() {
    if (!document.querySelector(CSS_CONTENT_SELECTOR)) return; // no widget
    for (var s = 0; s < document.styleSheets.length; s++) {
      try {
        neutralizeAppstleRules(document.styleSheets[s]);
      } catch (e) {
        /* cross-origin sheet: cssRules not readable, fallback above applies */
      }
    }
    rewriteAppstleStyleText();
  }

  /* CSSOM rewrites never sync back to the <style> element's textContent:
     the serialized DOM (outerHTML) keeps carrying the original
     `content: var(--as-why-*, --as-cb-text)` declarations, and rule-based
     scanners that statically parse the rendered DOM — instead of the live
     cascade — keep reporting F87 even though every computed pseudo-element
     is already `none`. The declarations are therefore also scrubbed from
     the style elements' text itself — and from the inline Appstle config
     script that carries the same CSS as a JS string (the widget's own
     source for every style it injects). Rewriting an already-executed
     script's text is inert, so nothing re-runs; if the widget re-reads the
     config later it now injects the neutralized CSS. Only Appstle's copy
     variables are touched; justify/spacing vars and decorative
     `content: ""` stay. */
  var APPSTLE_TEXT_DECL_RE =
    /content\s*:\s*var\(\s*--as-(?:why-(?:title|\d+)|cb-text)\s*(?:,[^)]*)?\)\s*[^;}]*;?/gi;

  function rewriteAppstleStyleText() {
    var nodes = document.querySelectorAll("style, script");
    for (var i = 0; i < nodes.length; i++) {
      var text = nodes[i].textContent;
      if (!text || (text.indexOf("--as-why-") === -1 && text.indexOf("--as-cb-text") === -1)) continue;
      var patched = text.replace(APPSTLE_TEXT_DECL_RE, "content:none!important;");
      if (patched !== text) nodes[i].textContent = patched;
    }
  }

  /* ------------------------------------------------------------------ */
  /* 4. Late-mounted controls (Seel chat renders in a shadow root):      */
  /*    a textarea without an accessible name gets the placeholder as    */
  /*    its aria-label, in the document and inside every open shadow root */
  /* ------------------------------------------------------------------ */
  function cssEscape(value) {
    return window.CSS && window.CSS.escape ? window.CSS.escape(value) : value.replace(/["\\\]]/g, "\\$&");
  }

  function hasAccessibleName(el) {
    if (el.getAttribute("aria-label") || el.getAttribute("aria-labelledby") || el.getAttribute("title")) return true;
    if (el.id) {
      try {
        if (document.querySelector('label[for="' + cssEscape(el.id) + '"]')) return true;
      } catch (e) { /* malformed id — fall through */ }
    }
    for (var parent = el.parentElement; parent; parent = parent.parentElement) {
      if (parent.tagName === "LABEL") return true;
    }
    return false;
  }

  function fixUnnamedTextareas(root) {
    var areas = root.querySelectorAll("textarea");
    for (var i = 0; i < areas.length; i++) {
      var area = areas[i];
      if (hasAccessibleName(area)) continue;
      var placeholder = (area.getAttribute("placeholder") || "").trim();
      area.setAttribute("aria-label", placeholder || "Message");
    }
  }

  /* ------------------------------------------------------------------ */
  /* 5. Judge.me rating summary, average links and helpful votes:        */
  /*    the visible label must LEAD the accessible name (F96)            */
  /* ------------------------------------------------------------------ */
  var JMD_LINK_SELECTOR = 'a[class*="jm-average-rating"]';
  var JMD_CLUSTER_SELECTOR = 'div[role="button"][aria-label*=" reviews with"]';
  var JM_VOTING_SELECTOR = 'button[class*="jm-voting"]';
  var NEW_WINDOW_SUFFIX = "(opens in a new window)";

  function visibleText(el) {
    var text = el.innerText || el.textContent || "";
    return text.replace(/[\u2605\u2606\u2B50]/g, " ").replace(/\s+/g, " ").trim();
  }

  /** Rebuild each name as "visible label, stored base" — a suffix-appending
      patch (new-window warning) or a changing visible label (review counts)
      must never accumulate stale prefixes, and the label has to lead the
      name for the F96 check to pass. */
  function prependVisibleText(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var current = el.getAttribute("aria-label") || "";
      var base = el.getAttribute(BASE_LABEL_ATTR);
      if (!base) {
        base = current;
        el.setAttribute(BASE_LABEL_ATTR, base);
      }
      // The new-window patch appends its suffix once, state-guarded; fold it
      // into the stored base so later rebuilds never drop it.
      if (current.indexOf(NEW_WINDOW_SUFFIX) !== -1 && base.indexOf(NEW_WINDOW_SUFFIX) === -1) {
        base = (base ? base + " " : "") + NEW_WINDOW_SUFFIX;
        el.setAttribute(BASE_LABEL_ATTR, base);
      }
      var visible = visibleText(el);
      if (!visible) continue;
      var name = visible + (base ? ", " + base : "");
      if (current !== name) {
        el.setAttribute("aria-label", name);
      }
    }
  }

  function fixJudgemeNames(root) {
    prependVisibleText(root.querySelectorAll(JMD_LINK_SELECTOR));
    prependVisibleText(root.querySelectorAll(JMD_CLUSTER_SELECTOR));
  }

  /* The voting buttons show the helpful count ("0", "2"…) as their only
     visible text; it must LEAD the announced name so speech control can
     target it (a trailing "(0)" is still flagged by the scanner). The
     count changes on click, so the name is rebuilt from the stored base
     label on every pass. */
  function fixJudgemeVoting(root) {
    var buttons = root.querySelectorAll(JM_VOTING_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var base = button.getAttribute(BASE_LABEL_ATTR);
      if (!base) {
        base = (button.getAttribute("aria-label") || "")
          .replace(/\d+/g, "").replace(/\s+/g, " ").trim() || "Mark as helpful";
        button.setAttribute(BASE_LABEL_ATTR, base);
      }
      var count = (button.textContent || "").match(/\d+/);
      var name = count ? count[0] + ", " + base : base;
      if (button.getAttribute("aria-label") !== name) {
        button.setAttribute("aria-label", name);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* 6. WCAG 2.4.7 / F78: Judge.me controls ship `outline: none`         */
  /* ------------------------------------------------------------------ */
  var FOCUS_STYLE_ID = "a11y-focus-styles";
  /* Mid-luminance accent for widget focus outlines (relative luminance
     ≈ 0.24): 3.6:1 against #fff and 5.8:1 against #000 — ≥3:1 against BOTH
     extremes. currentColor is wrong for the Tailwind widgets: the "Check
     Out" CTA resolves it to its white text while the page area around the
     button (and the background the scanner resolves, falling back to the
     white body) is white — a white outline on white is invisible to real
     keyboard users and reported as "outline-color: matches background".
     A fixed in-band color is safe whatever background each engine ends up
     resolving. Judge.me keeps currentColor (dark text on white, verified
     to clear the scan). */
  var WIDGET_FOCUS_COLOR = "#e06000";

  function injectDocumentFocusStyle() {
    if (document.getElementById(FOCUS_STYLE_ID)) return;
    var style = document.createElement("style");
    style.id = FOCUS_STYLE_ID;
    style.textContent =
      // Judge.me resets outlines on its own controls; both pseudo-classes
      // are written because rule-based scanners judge the :focus state
      // while :focus-visible is the keyboard state users actually see.
      ".jm-media-grid__item:focus,.jm-media-grid__item:focus-visible," +
      ".jdgm-sort-dropdown:focus,.jdgm-sort-dropdown:focus-visible," +
      "button.jm-button:focus,button.jm-button:focus-visible" +
      "{outline:2px solid currentColor!important;outline-offset:2px;}" +
      // Tailwind-built app widgets ("Check Out" cart/chat buttons,
      // disclosure buttons) reset outlines through the outline-none
      // utility — computed "transparent solid 2px" in every state. The
      // fragments below are utility-class names the theme never uses, so
      // only keyboard focus inside those widgets is affected. Elements
      // inside shadow roots cannot be reached from the document sheet and
      // are covered by the per-root rule below.
      "button[class*='w-full']:focus-visible,a[class*='w-full']:focus-visible," +
      "button[class*='text-left']:focus-visible,a[class*='text-left']:focus-visible" +
      "{outline:2px solid " + WIDGET_FOCUS_COLOR + "!important;outline-offset:2px;}";
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------------ */
  /* 7. F78 in shadow roots: per-root :focus-visible rule + a focusin    */
  /*    polyfill painting inline outlines where computed ones die        */
  /* ------------------------------------------------------------------ */
  var ROOT_FOCUS_STYLE_CLASS = "a11y-shadow-focus-style";

  function injectRootFocusStyle(root) {
    if (!root.querySelector) return;
    if (root.querySelector("style." + ROOT_FOCUS_STYLE_CLASS)) return;
    var style = document.createElement("style");
    style.className = ROOT_FOCUS_STYLE_CLASS;
    // Fixed in-band color instead of currentColor: white-text CTAs inside
    // shadow widgets (Affirm module and friends) would resolve currentColor
    // to white on a white page — the same invisible-ring failure seen on
    // the "Check Out" button. See WIDGET_FOCUS_COLOR above.
    style.textContent =
      ":focus-visible{outline:2px solid " + WIDGET_FOCUS_COLOR + "!important;outline-offset:2px;}";
    root.insertBefore(style, root.firstChild);
  }

  var OUTLINED_ATTR = "data-a11y-outlined";

  function focusOutlinePolyfill(event) {
    var el = event.target;
    if (!el || !el.matches) return; // document / retargeted shadow host
    if (!el.matches(":focus-visible")) return; // keyboard only, mouse stays clean
    var computed = window.getComputedStyle(el);
    if (computed.outlineStyle !== "none" && computed.outlineStyle !== "hidden") return;
    if (computed.boxShadow !== "none") return; // widget already paints its own ring
    el.style.outline = "2px solid " + WIDGET_FOCUS_COLOR;
    el.style.outlineOffset = "2px";
    el.setAttribute(OUTLINED_ATTR, "");
  }

  function focusOutlineReset(event) {
    var el = event.target;
    if (!el || !el.removeAttribute || !el.hasAttribute(OUTLINED_ATTR)) return;
    el.style.outline = "";
    el.style.outlineOffset = "";
    el.removeAttribute(OUTLINED_ATTR);
  }

  var focusListenersBound = false;

  function bindFocusPolyfill() {
    if (focusListenersBound) return;
    focusListenersBound = true;
    // capture phase: focusin/focusout are composed events, so keyboard
    // focus arriving inside open shadow roots is caught from the document
    document.addEventListener("focusin", focusOutlinePolyfill, true);
    document.addEventListener("focusout", focusOutlineReset, true);
  }

  /* ------------------------------------------------------------------ */
  /* 8. WCAG 1.4.3 / F24: explicit color on third-party anchors          */
  /* ------------------------------------------------------------------ */
  var WIDGET_LINK_SELECTOR =
    "a[href*='affirm.com'], a[href*='studentbeans.com'], " +
    "a[href*='beansid.com'], a.sb-cw__link-about";
  var UA_LINK_COLORS = {"rgb(0, 0, 238)": true, "rgb(85, 26, 139)": true};

  function fixExplicitLinkColors(root) {
    if (!root.querySelectorAll) return;
    var anchors = root.querySelectorAll(WIDGET_LINK_SELECTOR);
    for (var i = 0; i < anchors.length; i++) {
      var anchor = anchors[i];
      var color = window.getComputedStyle(anchor).color;
      // A UA-default link color means the widget stylesheet has not been
      // applied yet — freezing it would paint the raw blue. The observer
      // re-runs this pass when the widget finishes rendering.
      if (UA_LINK_COLORS[color]) continue;
      anchor.style.color = color;
      var spans = anchor.querySelectorAll("span");
      for (var j = 0; j < spans.length; j++) spans[j].style.color = color;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Runner: initial pass + debounced observer passes over the document  */
  /* and every open shadow root                                          */
  /* ------------------------------------------------------------------ */
  var observer = null;
  var observedRoots = typeof WeakSet === "function" ? new WeakSet() : null;
  var OBSERVER_CONFIG = {
    childList: true,
    subtree: true,
    characterData: true, // Judge.me helpful counts update text nodes
    attributes: true,    // the Seel chat swaps its placeholder in place, and
    attributeFilter: ["placeholder", "aria-label"] // the new-window patch appends its suffix
  };

  function collectShadowRoots(root, found) {
    if (!root.querySelectorAll) return found;
    var elements = root.querySelectorAll("*");
    for (var i = 0; i < elements.length; i++) {
      var shadow = elements[i].shadowRoot;
      if (shadow) {
        found.push(shadow);
        collectShadowRoots(shadow, found);
      }
    }
    return found;
  }

  function observeRoot(root) {
    if (!observer || !observedRoots || observedRoots.has(root)) return;
    observedRoots.add(root);
    observer.observe(root, OBSERVER_CONFIG);
  }

  function patch() {
    try {
      injectDocumentFocusStyle();
      bindFocusPolyfill();
      fixUnnamedTextareas(document);
      fixJudgemeAlt(document);
      fixJudgemeNames(document);
      fixJudgemeVoting(document);
      fixRatingRole(document);
      fixAppstleCssContent(document);
      fixExplicitLinkColors(document);
      neutralizeAppstleCssRules();
      var roots = collectShadowRoots(document, []);
      for (var i = 0; i < roots.length; i++) {
        fixUnnamedTextareas(roots[i]);
        injectRootFocusStyle(roots[i]);
        fixExplicitLinkColors(roots[i]);
        observeRoot(roots[i]);
      }
    } catch (e) {
      /* a patch must never break the page */
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      patch();
    }, 200);
  }

  function startObserver() {
    if (typeof MutationObserver === "undefined" || !document.documentElement) return;
    observer = new MutationObserver(schedule);
    // documentElement (not body): Appstle appends its <style> to <head>
    observer.observe(document.documentElement, OBSERVER_CONFIG);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      patch();
      startObserver();
    });
  } else {
    patch();
    startObserver();
  }
})();
